import React, { useEffect, useState } from 'react';

import './RydProjectContainers.css';
import '../index.css';

import InputLineContainer from './InputLine';
import { RydProject } from '../model/project';
import { RollRedrawObserver } from '../utils/RollRedrawObserver';
import { useAudioContextStore } from '../stores/AudioContextStore';
import { useMixerStore } from '../stores/MixerStore';
import {
    CaretController,
} from '../utils/ScrollController';
import { ProjectMainOptions } from './dialogs/ProjectMainOptions';
import { useZoomFactor } from '../stores/ZoomStore';
import {
    useSetWorkspaceBookmarks,
    useSetWorkspaceComputedContextFrame,
    useSetWorkspaceContextPlane,
    useSetWorkspaceInstrumentAssignments,
    useSetWorkspaceLines,
    useSetWorkspaceTracks,
    useSetWorkspaceUIState,
} from '../stores/WorkspaceStore';
import { RydInit } from '../init/RydInit';
import { RydKeybindingsInit } from '../init/RydKeybindingsInit';


export function CurrentProject({
    project,
    toggleLibraryCollapse,
    toggleInspectorCollapse,
    toggleMixerCollapse,
    initialized,
    setInitialized
}: {
    project: RydProject,
    toggleLibraryCollapse: () => void,
    toggleInspectorCollapse: () => void,
    toggleMixerCollapse: () => void,
    initialized: boolean,
    setInitialized: (v: boolean) => void,
}) {
    const [redrawObserver, setRedrawObserver] = useState<RollRedrawObserver>();
    const [caretController, setCaretController] = useState<CaretController>();
    const audioContextStore = useAudioContextStore();
    const mixerStore = useMixerStore();
    const zoomFactor = useZoomFactor();
    const setWorkspaceTracks = useSetWorkspaceTracks();
    const setWorkspaceContextPlane = useSetWorkspaceContextPlane();
    const setWorkspaceComputedContextFrame = useSetWorkspaceComputedContextFrame();
    const setWorkspaceLines = useSetWorkspaceLines();
    const setWorkspaceUIState = useSetWorkspaceUIState();
    const setWorkspaceInstrumentAssignments = useSetWorkspaceInstrumentAssignments();
    const setWorkspaceBookmarks = useSetWorkspaceBookmarks();

    useEffect(() => {
        if (!project) return;
        if (!caretController) return;
        RydKeybindingsInit.initKeybindings(project, caretController);
    }, [project, caretController]);

    useEffect(() => {
        RydInit.initAudioContext(audioContextStore);
    }, [audioContextStore]);

    useEffect(() => {
        RydInit.initMixer(audioContextStore, mixerStore);
    }, [mixerStore.mixer, audioContextStore]);

    useEffect(() => {
        return RydInit.initWorkspaceState(
            project,
            audioContextStore,
            mixerStore,
            setWorkspaceTracks,
            setWorkspaceContextPlane,
            setWorkspaceComputedContextFrame,
            setWorkspaceLines,
            setWorkspaceUIState,
            setWorkspaceInstrumentAssignments,
            setWorkspaceBookmarks,
            initialized,
            setInitialized
        );

    }, [project, mixerStore.mixer, audioContextStore]);

    useEffect(() => {
        RydInit.initScrollAndCarret(
            project,
            zoomFactor,
            setCaretController,
            setRedrawObserver
        );
        return () => {
            caretController?.clearListeners();
            caretController?.scrollController?.clearListeners();
        }
    }, []);

    useEffect(() => {
        caretController?.setZoomFactor(zoomFactor);
    }, [zoomFactor]);

    if (!caretController) return null;
    if (!redrawObserver) return null;

    if (!initialized && project) {
        console.log('INIT...');
        return (<>
            Loading project...</>
        );
    }

    return (
        <div className="ryd-current-project--wrapper">
            {caretController &&
                <>
                    <ProjectMainOptions
                        project={project}
                        toggleLibraryCollapse={toggleLibraryCollapse}
                        toggleInspectorCollapse={toggleInspectorCollapse}
                        toggleMixerCollapse={toggleMixerCollapse}
                        caretController={caretController}
                    />
                </>
            }
            <div className='ryd-project-workbench'>
                <div className="ryd-project-workbench--lines-wrapper">
                    <div id="line-elements">
                        <InputLineContainer
                            project={project}
                            redrawObserver={redrawObserver}
                            caretController={caretController}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
